import { classifyEntity, type GraphGroup, type HuntGraph, type NodeRole } from './hunt-graph'
import { signalNodeId, type PromotedSubgraph, type Subgraph } from './signal-subgraphs'
import { timeSpan } from './local-time'

/**
 * A storyline (connected-signals subgraph) for the storyline canvas
 * (decision S33, components/signal/force): every node carries a `glyph` —
 * signals are sonar hexes (severity core, ×N → staggered pings), suspects
 * emitters (one crescent per signal fired), targets receiver reticles (one
 * ring per hit), compromised both — in its role colour; edges directed
 * suspect → signal → target. Tokens only (`--viz-role-*`, `--bui-sev-*`).
 */
export const ROLE_TONE = {
  signal: 'var(--viz-role-signal)',
  suspect: 'var(--viz-role-suspect)',
  target: 'var(--viz-role-target)',
  compromised: 'var(--viz-role-compromised)',
} as const

export const STORYLINE_LEGEND: { label: string; tone: string; role: NodeRole }[] = (
  ['suspect', 'target', 'compromised', 'signal'] as const
).map((role) => ({ label: role, tone: ROLE_TONE[role], role }))

/** The legend's key to the glyphs. */
export const STORYLINE_LEGEND_NOTE = 'rings = hits · pings = severity'

/** Signal pings per cycle: one per merged signal, capped. */
const MAX_PINGS = 4

const CAPTION_MAX = 40
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

export function subgraphToGraph(sg: Subgraph): HuntGraph {
  const hits = new Map<string, number>()
  const fired = new Map<string, number>()
  for (const l of sg.links) {
    hits.set(l.to, (hits.get(l.to) ?? 0) + 1)
    fired.set(l.from, (fired.get(l.from) ?? 0) + 1)
  }
  return {
    nodes: [
      ...sg.signals.map((s) => ({
        id: signalNodeId(s.uuid),
        kind: 'other' as const,
        value: s.detectionName,
        field: 'Signal',
        expanded: false,
        tone: ROLE_TONE.signal,
        glyph: { role: 'signal' as const, severity: s.severity, pings: Math.min(MAX_PINGS, Math.max(1, s.count)) },
        detail: { tactic: s.tactic, technique: s.technique, start: s.start },
        ...(s.count > 1 ? { badge: `×${s.count}` } : {}),
        group: sg.key,
        title: `${s.detectionName || 'signal'} · ${s.tactic || 'no tactic'}${s.count > 1 ? ` · ×${s.count}` : ''} · ${timeSpan(s.start, s.end)} · ${s.severity}`,
      })),
      ...sg.entities.map((e) => ({
        id: e.id,
        kind: classifyEntity(e.field, e.value) ?? ('other' as const),
        value: e.value,
        field: e.field,
        expanded: false,
        tone: ROLE_TONE[e.role],
        glyph: { role: e.role, kind: e.field, hits: hits.get(e.id) ?? 0, fired: fired.get(e.id) ?? 0 },
        group: sg.key,
        title: `${e.role} · ${e.field.toLowerCase()} · ${e.value}`,
      })),
    ],
    edges: sg.links.map((l) => ({ from: l.from, to: l.to, viaQuery: '', directed: true })),
  }
}

export function storylinesCanvas(list: readonly PromotedSubgraph[]): { graph: HuntGraph; groups: GraphGroup[] } {
  const parts = list.map(subgraphToGraph)
  return {
    graph: { nodes: parts.flatMap((p) => p.nodes), edges: parts.flatMap((p) => p.edges) },
    // Caption = the entity (short, distinct per storyline); the full subgraph_name is the label.
    groups: list.map((sg) => {
      const main = sg.entityName ? sg.entities.find((e) => e.value === sg.entityName) : undefined
      return {
        id: sg.key,
        caption: `${clip(sg.entityName || sg.title, CAPTION_MAX)} · ${sg.score}${sg.watched ? ' ★' : ''}`,
        label: `${sg.title} · ${sg.score}`,
        ...(main ? { mainId: main.id } : {}),
      }
    }),
  }
}
